import { Film, Music2 } from 'lucide-react';

import { SwatchCardGrid } from '@/components/ai-tools/content-sections';
import { SampleGallery } from '@/components/ai-tools/sample-gallery';
import { localized, type DuoVideoTool } from '@/config/ai-tools';
import type { SlotEntry } from '@/lib/store/recommendation-slot';
import type { ToolSummary } from '@/modules/directory/types';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';
import { DuoVideoStudio } from './duo-video-studio';
import { ToolPageFrame } from './tool-page-frame';
import { resolveToolCopy } from './tool-copy';

/**
 * Tool page for a two-photo video generator (Hotel Lobby AI rap duo, Zombie
 * Couple Video, and siblings); wording per format comes from `resolveToolCopy`.
 * The shared page rhythm lives in `ToolPageFrame`; this supplies the studio,
 * the stage gallery and the per-format wording.
 */
export function DuoVideoToolPage({
  tool,
  slot,
  recentRenders,
}: {
  tool: DuoVideoTool;
  slot: SlotEntry<ToolSummary>[];
  recentRenders: number | null;
}) {
  const locale = getLocale();
  const t = (value: Parameters<typeof localized>[0]) => localized(value, locale);
  const copy = resolveToolCopy(tool, locale);

  return (
    <ToolPageFrame
      tool={tool}
      slot={slot}
      recentRenders={recentRenders}
      heroExamples={tool.examples.map((example) => ({ ...example, label: t(example.label) }))}
      studio={(onNeedCredits) => <DuoVideoStudio tool={tool} recommendations={slot} onNeedCredits={onNeedCredits} />}
      trustItem={{
        icon: tool.format === 'rap-duo' ? Music2 : Film,
        title: copy.trustTitle,
        body: copy.trustBody,
      }}
      gallery={
        tool.examples.length > 0
          ? {
              title: copy.galleryTitle,
              description: copy.galleryDescription,
              content: (
                <SampleGallery
                  items={tool.examples.map((example) => {
                    const stage = tool.stages.find((item) => item.id === example.stage);
                    return {
                      video: example.video,
                      poster: example.poster,
                      title: t(example.label),
                      description: example.description ? t(example.description) : undefined,
                      tag: stage ? t(stage.label) : undefined,
                    };
                  })}
                />
              ),
            }
          : null
      }
      options={{
        eyebrow: m['directory.ai_tools.eyebrow.stages'](),
        title: copy.stagesTitle,
        content: (
          <SwatchCardGrid
            items={tool.stages.map((stage) => ({
              id: stage.id,
              title: t(stage.label),
              body: t(stage.description),
              swatch: stage.swatch,
              image: stage.image,
            }))}
          />
        ),
      }}
      ideasTitle={copy.ideasTitle}
      relatedTitle={copy.relatedTitle}
      costs={{
        caption: m['directory.ai_tools.costs.title'](),
        labelHeader: m['directory.ai_tools.costs.quality'](),
        valueHeader: m['directory.ai_tools.costs.credits'](),
      }}
      packsUnit="video"
      standardCredits={Math.min(...tool.qualities.map((quality) => quality.credits))}
      cta={{ title: m['directory.ai_tools.cta_title'](), description: copy.ctaDescription, button: copy.ctaButton }}
    />
  );
}
